const ProductShopSelector = require("../../../src/includes/shops/ProductShopSelector").default;
const React = require("react");
const { usePrintify } = require("../context/PrintifyContext");
const { useResource } = require("../hooks/useResource");
const { PageHeader, Button, ResourceState, ProductCard } = require("./ui");
const h = React.createElement;

const PAGE_SIZE = 24;
const DEFAULT_FILTERS = { keyword: "", category: "", tag: "", status: "", minPrice: "", maxPrice: "", sortBy: "updatedAt", sortOrder: "desc" };

const ProductCollectionPage = ({ title, description, supplierId, imported = false }) => {
  const { api, t } = usePrintify();
  const [shopIds, setShopIds] = React.useState(undefined);
  const [filters, setFilters] = React.useState({ ...DEFAULT_FILTERS });
  const [query, setQuery] = React.useState({});
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState([]);
  const [busy, setBusy] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [mutationError, setMutationError] = React.useState("");
  const params = { ...query, page, limit: PAGE_SIZE };
  const resource = useResource(() => imported
    ? api.getImportedProducts(params)
    : supplierId
      ? api.getSupplierProducts(supplierId, params)
      : api.getProducts(params), [api, imported, supplierId, page, JSON.stringify(query)]);
  const products = resource.data?.products || [];
  const total = Number(resource.data?.total || 0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const change = (key) => (event) => setFilters((old) => ({ ...old, [key]: event.target.value }));
  const toggle = (id) => setSelected((old) => old.includes(id) ? old.filter((value) => value !== id) : [...old, id]);
  const mutate = async (product, action, success) => {
    setBusy(product._id); setMessage(""); setMutationError("");
    try { await action(); setMessage(success); await resource.reload(); }
    catch (error) { setMutationError(error.message); }
    finally { setBusy(""); }
  };
  const importSelected = async () => {
    if (!selected.length || !window.confirm(t("h_printify_confirm_bulk_import", "Import selected Printify products into Elakay?"))) return;
    setBusy("selected"); setMessage(""); setMutationError("");
    let importedCount = 0;
    try {
      for (const id of selected) {
        await api.importProduct({ boutiqueId: shopIds, dropshippingProductId: id, selectedVariants: [], autoSync: true });
        importedCount += 1;
      }
      setSelected([]);
      setMessage(t("h_printify_bulk_import_success", "{count} products imported into Elakay.", { count: importedCount }));
      await resource.reload();
    } catch (error) { setMutationError(error.message); }
    finally { setBusy(""); }
  };

  return h(React.Fragment, null,
    h(PageHeader, { title, description }),
    !imported && h(ProductShopSelector, { value: shopIds, onChange: setShopIds }),
    h("form", { className: "pyds-toolbar", onSubmit: (event) => { event.preventDefault(); setPage(1); setQuery(filters); } },
      h("input", { className: "pyds-input", value: filters.keyword, onChange: change("keyword"), placeholder: t("h_printify_search", "Search product or SKU"), "aria-label": t("h_printify_search", "Search product or SKU") }),
      h("input", { className: "pyds-input", value: filters.category, onChange: change("category"), placeholder: t("h_printify_category", "Category") }),
      h("input", { className: "pyds-input", value: filters.tag, onChange: change("tag"), placeholder: t("h_printify_tag", "Tag") }),
      h("input", { className: "pyds-input", type: "number", min: "0", value: filters.minPrice, onChange: change("minPrice"), placeholder: t("h_printify_min_price", "Min price") }),
      h("input", { className: "pyds-input", type: "number", min: "0", value: filters.maxPrice, onChange: change("maxPrice"), placeholder: t("h_printify_max_price", "Max price") }),
      h("select", { className: "pyds-input", value: filters.status, onChange: change("status") },
        h("option", { value: "" }, t("h_printify_all_statuses", "All availability")),
        h("option", { value: "active" }, t("h_printify_active", "Active")),
        h("option", { value: "unavailable" }, t("h_printify_unavailable", "Unavailable")),
        h("option", { value: "imported" }, t("h_printify_imported", "Imported"))
      ),
      h("select", { className: "pyds-input", value: filters.sortBy, onChange: change("sortBy") },
        h("option", { value: "updatedAt" }, t("h_printify_sort_updated", "Recently updated")),
        h("option", { value: "title" }, t("h_printify_sort_title", "Title")),
        h("option", { value: "costPrice" }, t("h_printify_sort_cost", "Production cost")),
        h("option", { value: "suggestedSalePrice" }, t("h_printify_sort_price", "Sale price"))
      ),
      h("select", { className: "pyds-input", value: filters.sortOrder, onChange: change("sortOrder") }, h("option", { value: "desc" }, t("h_printify_desc", "Descending")), h("option", { value: "asc" }, t("h_printify_asc", "Ascending"))),
      h(Button, { type: "submit" }, t("h_printify_filter", "Filter")),
      h(Button, { variant: "secondary", disabled: JSON.stringify(filters) === JSON.stringify(DEFAULT_FILTERS) && !Object.keys(query).length, onClick: () => { setFilters({ ...DEFAULT_FILTERS }); setQuery({}); setPage(1); setSelected([]); } }, t("h_printify_reset", "Reset")),
      !supplierId && !imported ? h(Button, { variant: "secondary", disabled: busy === "all", onClick: async () => { setBusy("all"); try { await api.syncAll(); await resource.reload(); } catch (error) { setMutationError(error.message); } finally { setBusy(""); } } }, busy === "all" ? t("h_printify_syncing", "Syncing…") : t("h_printify_sync_catalog", "Sync catalog")) : null
    ),
    !imported && selected.length ? h("div", { className: "pyds-selection-bar" },
      h("strong", null, t("h_printify_selected_count", "{count} selected", { count: selected.length })),
      h(Button, { disabled: busy === "selected", onClick: importSelected }, busy === "selected" ? t("h_printify_importing", "Importing…") : t("h_printify_import_selected", "Import selected")),
      h(Button, { variant: "secondary", onClick: () => setSelected([]) }, t("h_printify_clear_selection", "Clear"))
    ) : null,
    message ? h("div", { className: "pyds-success" }, message) : null,
    mutationError ? h("div", { className: "pyds-alert", role: "alert" }, mutationError) : null,
    h(ResourceState, { loading: resource.loading, error: resource.error, empty: !products.length, reload: resource.reload, t },
      h("div", { className: "pyds-grid products" }, ...products.map((product) => h("div", { className: `pyds-select-card ${selected.includes(product._id) ? "selected" : ""}`.trim(), key: product._id },
        !imported && !product.elakayProductId ? h("label", { className: "pyds-select-check" }, h("input", { type: "checkbox", checked: selected.includes(product._id), onChange: () => toggle(product._id), "aria-label": t("h_printify_select_product", "Select {product}", { product: product.title }) })) : null,
        h(ProductCard, {
          product,
          t,
          busy: busy === product._id,
          onImport: imported || product.elakayProductId ? null : () => mutate(product, () => api.importProduct({ boutiqueId: shopIds, dropshippingProductId: product._id, selectedVariants: [], autoSync: true }), t("h_printify_import_success", "Product imported into Elakay.")),
          onSync: imported || product.elakayProductId ? () => mutate(product, () => api.syncProduct(product._id), t("h_printify_sync_success", "Product synchronized.")) : null,
        })
      )))
    ),
    totalPages > 1 ? h("nav", { className: "pyds-pagination", "aria-label": t("h_printify_pagination", "Product pagination") },
      h(Button, { variant: "secondary", disabled: page <= 1, onClick: () => { setPage((value) => value - 1); setSelected([]); } }, t("h_printify_previous", "Previous")),
      h("span", null, t("h_printify_page_count", "Page {page} of {total}", { page, total: totalPages })),
      h(Button, { variant: "secondary", disabled: page >= totalPages, onClick: () => { setPage((value) => value + 1); setSelected([]); } }, t("h_printify_next", "Next"))
    ) : null
  );
};

module.exports = { ProductCollectionPage };
